import { Archive } from 'lucide-react'
import { InvokeIcon } from '@renderer/components/invoke-icon'
import type { ActionRowModel } from '../hooks/staged-actions/use-action-row-model'
import {
  CELL_CONTEXT_MENU_TRIGGER,
  CellActionsCluster,
  CellActionsMenu,
  undoCellAction,
  type CellAction,
} from './cell-actions'
import { HOVER_CLUSTER_TRIGGER } from './cell-hover-buttons'

/**
 * What an action row offers in its current state, for both surfaces that offer
 * it — the hover cluster and the right-click menu.
 *
 * Reconnect and prerequisite CTAs need their labels, so those stay in the hover
 * card rather than shrinking to an ambiguous icon or a bare menu line.
 */
export function actionRowCellActions(model: ActionRowModel, trigger: string): Array<CellAction> {
  const { view, inArchiveWindow } = model
  if (!view) return []

  // One Undo for both countdown windows — a taken-back archive and a
  // reversible execution — differing only in which take-back it fires.
  if (inArchiveWindow || model.canUndo) {
    return [
      undoCellAction(() => (inArchiveWindow ? model.handleUndoArchive(trigger) : model.handleUndo(trigger)), {
        disabled: !inArchiveWindow && model.isUndoingThis,
      }),
    ]
  }

  // Archived and running rows have no quick move of their own: whatever is left
  // to do happens in the view the row opens.
  const actionable = !model.isArchived && !model.isInvoking
  // An answerable has nothing to invoke, but it keeps its dismissal (HIG-6042).
  // Answering and skipping both post to an endpoint that refuses an action the
  // server has already resolved, so a card settled behind the user's back would
  // otherwise sit in the Outbox forever.
  const showArchive = actionable && !model.isExecuted
  const showInvoke =
    actionable &&
    !model.isAnswerable &&
    !model.isExecuted &&
    !model.isReport &&
    !model.isLocked &&
    !model.connectionError &&
    !model.unsatisfiedPrerequisite

  return [
    ...(showArchive
      ? [
          {
            key: 'archive',
            // An ask is a request, not a draft to file away, and the card
            // already calls passing on it skipping.
            label: model.isAnswerable ? 'Dismiss' : 'Archive action',
            icon: <Archive />,
            onSelect: () => model.handleClear(trigger),
          } satisfies CellAction,
        ]
      : []),
    ...(showInvoke
      ? [
          {
            key: 'invoke',
            label: model.actionHasError ? 'Retry' : view.invokeLabel,
            icon: <InvokeIcon icon={view.invokeIcon} />,
            tone: 'brand',
            disabled: model.isInvokingThis,
            onSelect: () => model.handleInvoke(trigger),
          } satisfies CellAction,
        ]
      : []),
  ]
}

interface ActionRowHoverButtonsProps {
  model: ActionRowModel
  /** The row is in its collapsed Outbox form — the only shape whose controls live on the cluster. */
  collapsed: boolean
}

/**
 * The collapsed Outbox row's floating quick moves (Figma 8425:91176) — the
 * expanded cell keeps its footer buttons instead.
 *
 * Opening the row in its own window is offered on every collapsed row, so the
 * states with no move to make (running, archived, a question that only wants
 * opening) carry a popout-only cluster.
 *
 * During the undo windows the buttons morph into Undo in place — archive turns
 * into its own take-back under the pointer that clicked it. The take-back is
 * hover-gated like every other quick move (HIG-5928); the row draws its own
 * countdown, so the window stays legible without the button outliving the
 * pointer. Popout drops out for the duration, since a countdown is not the
 * moment to navigate away.
 */
export function ActionRowHoverButtons({ model, collapsed }: ActionRowHoverButtonsProps) {
  if (!model.view || !collapsed) return null

  const inUndoWindow = model.inArchiveWindow || model.canUndo

  return (
    <CellActionsCluster
      actions={actionRowCellActions(model, HOVER_CLUSTER_TRIGGER)}
      onPopout={inUndoWindow ? undefined : () => model.openActionViewInWindow(HOVER_CLUSTER_TRIGGER)}
    />
  )
}

/** The action row's right-click menu — the cluster's moves, on every row shape. */
export function ActionRowContextMenuItems({ model }: { model: ActionRowModel }) {
  return (
    <CellActionsMenu
      actions={actionRowCellActions(model, CELL_CONTEXT_MENU_TRIGGER)}
      onPopout={() => model.openActionViewInWindow(CELL_CONTEXT_MENU_TRIGGER)}
    />
  )
}
